// Heartbeat engine. Posts liveness + cache size to /player/heartbeat every
// HEARTBEAT_INTERVAL_MS. Payload is strictly limited to fields the API DTO
// accepts: appVersion, storageUsedBytes, storageTotalBytes (all optional).
// Other client-side metrics (manifest_hash, error counts, etc.) live in the
// debug overlay until the API DTO is intentionally extended.

import { API_BASE, APP_VERSION, HEARTBEAT_INTERVAL_MS } from './config';
import { getCacheStats, getStorageQuota } from './cache';

export interface HeartbeatEngineEvents {
  onSuccess?: () => void;
  onUnauthorized?: () => void;
  onError?: (err: Error) => void;
}

export interface HeartbeatEngine {
  start: () => void;
  stop: () => void;
  sendNow: () => Promise<void>;
}

interface HeartbeatPayload {
  appVersion: string;
  storageUsedBytes: number;
  storageTotalBytes?: number;
}

export function createHeartbeatEngine(
  token: string,
  events: HeartbeatEngineEvents,
): HeartbeatEngine {
  let intervalId: number | null = null;
  let stopped = false;

  async function sendNow(): Promise<void> {
    if (stopped) return;

    // Gather only fields the API DTO accepts.
    const stats = await getCacheStats();
    const quota = await getStorageQuota();

    const body: HeartbeatPayload = {
      appVersion: APP_VERSION,
      storageUsedBytes: stats.totalBytes,
    };
    if (quota.quota !== null) {
      body.storageTotalBytes = quota.quota;
    }

    try {
      const res = await fetch(`${API_BASE}/player/heartbeat`, {
        method: 'POST',
        headers: {
          Authorization: `Device ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (stopped) return;

      if (res.status === 401) {
        events.onUnauthorized?.();
        return;
      }
      if (!res.ok) {
        events.onError?.(new Error(`Heartbeat HTTP ${res.status}`));
        return;
      }
      events.onSuccess?.();
    } catch (err) {
      if (stopped) return;
      events.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }

  function start(): void {
    if (intervalId !== null) return;
    stopped = false;
    // First heartbeat right away (lets the dashboard show "online" without
    // waiting a full interval after pairing).
    void sendNow();
    intervalId = window.setInterval(() => {
      void sendNow();
    }, HEARTBEAT_INTERVAL_MS);
  }

  function stop(): void {
    stopped = true;
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  return { start, stop, sendNow };
}
